import { SourceImageDto } from "@repo/api/source-image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ImageDropdownMenu from "./ImageDropdownMenu";
import { Button } from "@/lib/components/Button/Button";
import { motion } from "motion/react";
import { AnimatePresence } from "motion/react";
import {
  SourceImageKey,
  useSourceImageActions,
} from "./creative-project-hooks";
import { SourceImageReplaceDialog } from "../SourceImageSelectorDialog";
import {
  IconExclamationCircle,
  IconLoader2,
  IconPhotoUp,
} from "@tabler/icons-react";
import { isSourceImageProcessing } from "@repo/api/utils/composition";

type Props = {
  sourceImageKey: SourceImageKey;
  primaryAction: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
  };
  showRemoveOption?: boolean;
  onImageClick?: () => void;
  onImageReplaced?: () => void;
};

export default function ImagePreview({
  sourceImageKey,
  primaryAction,
  showRemoveOption = true,
  onImageClick,
  onImageReplaced,
}: Props) {
  const {
    sourceImage,
    isReplaceDialogOpen,
    setIsReplaceDialogOpen,
    handleReplaceSelect,
    handleRemoveImage,
    handleDownloadImage,
    openUploadDialog,
    getInputProps,
  } = useSourceImageActions(sourceImageKey, { onImageReplaced });

  const [shouldAnimateInitial, setShouldAnimateInitial] = useState(false);
  const [prevSourceImageKey, setPrevSourceImageKey] = useState(sourceImageKey);

  // Should be a valid use case based on: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (prevSourceImageKey !== sourceImageKey) {
    setPrevSourceImageKey(sourceImageKey);
    setShouldAnimateInitial(true);
  }

  const isEndImage = sourceImageKey === "endSourceImageExternalId";

  return (
    <div className="relative w-full flex-1 overflow-hidden rounded-xl">
      {/* Do not add the input or dialog inside the AnimatePresence. It will break internal refs in react-dropzone. */}
      <input {...getInputProps()} />
      <SourceImageReplaceDialog
        isOpen={isReplaceDialogOpen}
        setIsOpen={setIsReplaceDialogOpen}
        handleSelect={handleReplaceSelect}
      />
      <AnimatePresence>
        <motion.div
          key={sourceImageKey}
          className="absolute inset-0"
          {...getImagePreviewAnimationProps(
            sourceImageKey,
            shouldAnimateInitial,
          )}
        >
          {sourceImage ? (
            <ImagePreviewInternal
              key={sourceImage.src}
              sourceImage={sourceImage}
              primaryAction={primaryAction}
              onUploadClick={openUploadDialog}
              onReplaceClick={() => setIsReplaceDialogOpen(true)}
              onDownloadClick={handleDownloadImage}
              onRemoveClick={
                isEndImage && showRemoveOption ? handleRemoveImage : undefined
              }
              onImageClick={onImageClick}
            />
          ) : (
            <ImagePreviewEmpty
              sourceImageKey={sourceImageKey}
              onReplaceClick={() => setIsReplaceDialogOpen(true)}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

type ImagePreviewInternalProps = {
  sourceImage: SourceImageDto;
  primaryAction: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
  };
  onUploadClick: () => void;
  onReplaceClick: () => void;
  onDownloadClick: () => void;
  onRemoveClick?: () => void;
  onImageClick?: () => void;
};

function ImagePreviewInternal({
  sourceImage,
  primaryAction,
  onUploadClick,
  onReplaceClick,
  onDownloadClick,
  onRemoveClick,
  onImageClick,
}: ImagePreviewInternalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setContainerSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setNaturalSize({
        width: e.currentTarget.naturalWidth,
        height: e.currentTarget.naturalHeight,
      });
    },
    [],
  );

  const renderedSize = useMemo(() => {
    if (
      naturalSize.width === 0 ||
      naturalSize.height === 0 ||
      containerSize.width === 0 ||
      containerSize.height === 0
    ) {
      return null;
    }

    const imageAspect = naturalSize.width / naturalSize.height;
    const containerAspect = containerSize.width / containerSize.height;

    if (imageAspect > containerAspect) {
      return {
        width: containerSize.width,
        height: containerSize.width / imageAspect,
      };
    } else {
      return {
        width: containerSize.height * imageAspect,
        height: containerSize.height,
      };
    }
  }, [naturalSize, containerSize]);

  return (
    <div
      ref={containerRef}
      onClick={onImageClick}
      className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-grey-150"
    >
      <div
        className="relative"
        style={{
          opacity: renderedSize ? 1 : 0,
          width: renderedSize?.width,
          height: renderedSize?.height,
        }}
      >
        <img
          src={sourceImage.src}
          onLoad={handleImageLoad}
          className="h-full w-full"
        />

        {isSourceImageProcessing(sourceImage) && <ImageGeneratingOverlay />}
        {sourceImage.status === "FAILED" && <ImageFailedOverlay />}

        <div className="absolute top-5 right-5 z-20">
          <ImageDropdownMenu
            onReplaceClick={onReplaceClick}
            onUploadClick={onUploadClick}
            onDownloadClick={onDownloadClick}
            onRemoveClick={onRemoveClick}
          />
        </div>

        <Button
          variant="outline"
          className="absolute right-5 bottom-5 z-20"
          size="sm"
          iconPosition="leading"
          icon={primaryAction.icon}
          onClick={primaryAction.onClick}
        >
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}

function ImagePreviewEmpty({
  sourceImageKey,
  onReplaceClick,
}: {
  sourceImageKey: SourceImageKey;
  onReplaceClick: () => void;
}) {
  const title =
    sourceImageKey === "startSourceImageExternalId"
      ? "Start image"
      : "End image (optional)";
  const description =
    sourceImageKey === "startSourceImageExternalId"
      ? "This image will be the starting point of the animation."
      : "The AI will generate a transition between your start image and this one.";

  const actionLabel =
    sourceImageKey === "startSourceImageExternalId"
      ? "Add start image"
      : "Add end image";

  return (
    <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl bg-grey-150">
      <p className="text-center font-semibold text-text-primary">{title}</p>
      <p className="mt-2 max-w-80 text-center text-sm text-text-secondary">
        {description}
      </p>
      <Button
        variant="outline"
        className="mt-6"
        size="sm"
        iconPosition="leading"
        icon={<IconPhotoUp />}
        onClick={onReplaceClick}
      >
        {actionLabel}
      </Button>
    </div>
  );
}

function getImagePreviewAnimationProps(
  sourceImageKey: SourceImageKey,
  shouldAnimateInitial: boolean,
) {
  return {
    transition: {
      type: "tween" as const,
      duration: 0.25,
    },
    initial: shouldAnimateInitial
      ? {
          opacity: 0,
          x: sourceImageKey === "startSourceImageExternalId" ? "-100%" : "100%",
        }
      : false,
    animate: { opacity: 1, x: 0 },
    exit: {
      opacity: 0,
      x: sourceImageKey === "startSourceImageExternalId" ? "-100%" : "100%",
    },
  };
}

function ImageGeneratingOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/50">
      <IconLoader2 size={24} className="animate-spin text-white" />
      <p className="mt-3 text-base font-semibold text-white">
        Generating image
      </p>
      <p className="mt-0.5 text-sm text-white">This takes about 1 minute</p>
    </div>
  );
}

function ImageFailedOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/50 text-center">
      <IconExclamationCircle size={24} className="text-white" />
      <p className="mt-3 text-base font-semibold text-white">
        Failed to generate image
      </p>
      <p className="mt-0.5 text-sm leading-normal text-white">
        Credits have been refunded.
        <br />
        Please try again or contact support.
      </p>
    </div>
  );
}
